import { useT } from '@transifex/react';
import type { JSX, ReactNode } from 'react';
import { Children, cloneElement } from 'react';
import { useFormContext } from 'react-hook-form';

import { cssClasses } from 'pkg/css/utils';

import { FormattedContent } from 'components/formatted-content';

import * as Input from 'components/form/inputs';

import * as css from './styles.css';

/**
 * Props interface for the Group component.
 */
export interface Props {
	label?: string;
	smallLabel?: boolean;
	description?: string;
	hint?: string;
	hasError?: boolean;
	errorMessage?: string;
	optional?: boolean;
	required?: boolean;
	readOnly?: boolean;
	disabled?: boolean;
	justify?: string;
	className?: string;
	testid?: string;
	children?: ReactNode;
	onClick?: () => void;
}

/**
 * Group component
 *
 * A form group component that organizes multiple form input elements under a common label and description.
 */

export function Group({
	label,
	smallLabel = false,
	description,
	hint,
	hasError,
	errorMessage,
	optional,
	required,
	disabled,
	readOnly,
	justify,
	testid,
	className,
	children,
	onClick,
}: Props): JSX.Element {
	const formContext = useFormContext();
	const t = useT();

	const hasValidationError =
		formContext &&
		Children.toArray(children).some((child: JSX.Element) => {
			if (!child.props.name) {
				return false;
			}

			let errLvl = formContext.formState.errors;

			const nameParts = (child.props.name as string).split('.');
			const inputName = nameParts.pop();

			for (let i = 0; i < nameParts.length; i++) {
				errLvl = errLvl?.[nameParts[i]] as any;
			}

			return errLvl?.[inputName];
		});

	if (disabled) {
		children = Children.map(children, (child: JSX.Element) =>
			cloneElement(child, {
				disabled,
			})
		);
	}

	const hasAnyError = hasError || hasValidationError || !!errorMessage;

	return (
		<fieldset
			onClick={onClick}
			className={cssClasses(
				css.group,
				readOnly && css.readOnly,
				disabled && css.disabled,
				hasAnyError && css.hasError,
				className && className,
				smallLabel ? css.smallLabel : ''
			)}
			style={{ justifyContent: justify }}
			data-has-error={hasAnyError || undefined}
			data-testid={testid}>
			{label && (
				<Input.Label>
					{label}
					{optional && <span>{t('Optional')}</span>}
					{required && <span>{t('Required')}</span>}
				</Input.Label>
			)}

			{description && (
				<Input.InfoText>
					<FormattedContent raw={description} allowedFormatters={['links']} />
				</Input.InfoText>
			)}
			{children && <div className={css.children}>{children}</div>}
			{errorMessage && (
				<Input.InfoText error testid={testid ? `${testid}.error` : undefined}>
					{errorMessage}
				</Input.InfoText>
			)}
			{hint && <Input.InfoText>{hint}</Input.InfoText>}
		</fieldset>
	);
}

interface GroupBackgroundProps {
	children: React.ReactNode;
}

export function GroupBackground({ children }: GroupBackgroundProps) {
	return <div className={css.groupBackground}>{children}</div>;
}
